import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  getAllowedLabTypes,
  getLabTypeName,
  getInitialLabTypeId,
  getFirstLabTypeId,
  shouldDisableDropdown,
  LAB_TYPES
} from "../utils/labTypeFilter";

const ScopeMasterPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Form state (for adding/editing)
  const [formData, setFormData] = useState({
    scopeid: null,
    labtypeid: '',
    disciplineid: '',
    scopetype: '',
    ptavailable: false,
    materialstested: '',
    componentparametertested: '',
    testmethodspecification: '',
    subgroup: '',
    groupid: ''
  });

  // Dropdown states for the form
  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scopeData, setScopeData] = useState([]);
  const [error, setError] = useState('');

  // Scope Type options
  const scopeTypeOptions = [
    'Qualitative',
    'Quantitative',
    'Calculated'
  ];

  // Loading and messages
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Filter state
  const [filterLabType, setFilterLabType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterGroup, setFilterGroup] = useState('');

  // 🔹 NEW: Separate states for filter dropdown options
  const [filterDisciplines, setFilterDisciplines] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);

  // Additional filter fields
  const [filterSearch, setFilterSearch] = useState('');
  const [filterComponent, setFilterComponent] = useState('');
  const [filterMaterial, setFilterMaterial] = useState('');
  const [filterTestMethod, setFilterTestMethod] = useState('');
  const [filterTestCode, setFilterTestCode] = useState('');

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLabType, setBulkLabType] = useState('');
  const [bulkDiscipline, setBulkDiscipline] = useState('');
  const [bulkGroup, setBulkGroup] = useState('');
  const [bulkScopeType, setBulkScopeType] = useState('');
  const [bulkPTAvailable, setBulkPTAvailable] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkGroups, setBulkGroups] = useState([]);

  // Pagination state - server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);

  // ---------- Form dropdown effects ----------
  useEffect(() => {
    if (formData.labtypeid) {
      fetchDisciplines(formData.labtypeid);
      setFormData(prev => ({ ...prev, disciplineid: '', groupid: '' }));
    }
  }, [formData.labtypeid]);

  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid) {
      fetchGroups(formData.labtypeid, formData.disciplineid);
    } else {
      setGroups([]);
    }
  }, [formData.labtypeid, formData.disciplineid]);

  // ---------- Filter dropdown effects (NEW) ----------
  // Fetch disciplines for filter when lab type changes
  useEffect(() => {
    if (filterLabType) {
      fetchFilterDisciplines(filterLabType);
    } else {
      setFilterDisciplines([]);
      setFilterDiscipline('');
    }
  }, [filterLabType]);

  // Fetch groups for filter when both lab type and discipline are selected
  useEffect(() => {
    if (filterLabType && filterDiscipline) {
      fetchFilterGroups(filterLabType, filterDiscipline);
    } else {
      setFilterGroups([]);
      setFilterGroup('');
    }
  }, [filterLabType, filterDiscipline]);

  // ---------- Bulk upload dropdown effects ----------
  useEffect(() => {
    if (bulkLabType && bulkDiscipline) {
      fetchBulkGroups(bulkLabType, bulkDiscipline);
    } else {
      setBulkGroups([]);
      setBulkGroup('');
    }
  }, [bulkLabType, bulkDiscipline]);

  // ---------- Initialization from localStorage ----------
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');

    let defaultLabTypeId = null;

    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));

      if (allowedIds.length > 0) {
        defaultLabTypeId = allowedIds[0];
      }
    } else if (storedLabTypeId) {
      defaultLabTypeId = Number(storedLabTypeId);
    }

    if (defaultLabTypeId) {
      setFormData(prev => ({
        ...prev,
        labtypeid: defaultLabTypeId
      }));
      setBulkLabType(defaultLabTypeId);
      setFilterLabType(defaultLabTypeId);   // also set filter default
      fetchDisciplines(defaultLabTypeId);
    }
  }, []);

  // ---------- Fetch data on filterLabType change ----------
  useEffect(() => {
    if (filterLabType) {
      fetchScopeList();
    }
  }, [filterLabType]);

  // ---------- Fetch on pagination change ----------
  useEffect(() => {
    if (filterLabType && scopeData.length > 0) {
      fetchScopeList();
    }
  }, [page, rowsPerPage]);

  // ---------- API functions ----------
  const fetchScopeList = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(filterComponent && { component: filterComponent }),
        ...(filterMaterial && { material: filterMaterial }),
        ...(filterTestMethod && { testMethod: filterTestMethod }),
        ...(filterTestCode && { search: filterTestCode }),
      };

      const response = await axios.get(`${API_BASE_URL}/admin-master/scope-master`, { params });

      if (response.data.status === 'success') {
        setScopeData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setError('');
      } else {
        setError('Failed to fetch scope list');
        setScopeData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching scope:', err);
      setError(err.response?.data?.message || 'Error fetching scope list');
      setScopeData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setDisciplines(response.data.data || []);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setDisciplines([]);
    }
  };

  const fetchGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setGroups(response.data.data || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  // ---------- NEW: Filter-specific fetch functions ----------
  const fetchFilterDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);
      if (response.data.status === 'success') {
        setFilterDisciplines(response.data.data || []);
      } else {
        setFilterDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching filter disciplines:', err);
      setFilterDisciplines([]);
    }
  };

  const fetchFilterGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setFilterGroups(response.data.data || []);
      } else {
        setFilterGroups([]);
      }
    } catch (err) {
      console.error('Error fetching filter groups:', err);
      setFilterGroups([]);
    }
  };

  const fetchBulkGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId }
      });
      if (response.data.status === 'success') {
        setBulkGroups(response.data.data || []);
      } else {
        setBulkGroups([]);
      }
    } catch (err) {
      console.error('Error fetching bulk groups:', err);
      setBulkGroups([]);
    }
  };

  // ---------- Handlers ----------
  const handleLabTypeFilterChange = (value) => {
    setFilterLabType(value);
    setFilterDiscipline('');      // reset discipline
    setFilterGroup('');           // reset group
    setFilterGroups([]);          // clear options
    setScopeData([]);
    setTotalCount(0);
    setPage(0);
  };

  const handleDisciplineFilterChange = (value) => {
    setFilterDiscipline(value);
    setFilterGroup('');           // reset group when discipline changes
    setScopeData([]);
    setTotalCount(0);
    setPage(0);
  };

  const handleGroupFilterChange = (value) => {
    setFilterGroup(value);
    setPage(0);
  };

  const handleSearchClick = () => {
    setPage(0);
    if (filterLabType) {
      fetchScopeList();
    } else {
      setError('Please select CAB Type to search');
    }
  };

  const handleClearFilters = () => {
    setFilterLabType('');
    setFilterDiscipline('');
    setFilterGroup('');
    setFilterComponent('');
    setFilterMaterial('');
    setFilterTestMethod('');
    setFilterTestCode('');
    setFilterDisciplines([]);
    setFilterGroups([]);
    setScopeData([]);
    setTotalCount(0);
    setPage(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 1000000,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(filterComponent && { component: filterComponent }),
        ...(filterMaterial && { material: filterMaterial }),
        ...(filterTestMethod && { testMethod: filterTestMethod }),
        ...(filterTestCode && { search: filterTestCode }),
      };

      const response = await axios.get(`${API_BASE_URL}/admin-master/scope-master`, { params });

      if (response.data.status !== "success") {
        setError("Failed to export data");
        return;
      }

      const allData = response.data.data || [];
      const exportData = allData.map((row, index) => {
        const cabType = LAB_TYPES.find(c => String(c.id) === String(row.LabTypeid));
        return {
          "S.No": index + 1,
          "CAB Type": cabType?.name || "N/A",
          "Test Code": row.TestCode || "N/A",
          "Discipline": row.DisciplineName || "N/A",
          "Group": row.GroupName || "N/A",
          "Component/Parameter Tested": row.TypeOfTest || "N/A",
          "Materials or Products Tested": row.GroupTested || "N/A",
          "Test Method Specification": row.ScopeStandard || "N/A",
          "Scope Type": row.Category || "N/A",
          "PT Available": (row.PTAvailable === '1' || row.PTAvailable === "Yes") ? "Yes" : "No",
          "Created On": row.InsertDate ? new Date(row.InsertDate).toLocaleDateString() : "N/A"
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "ScopeData");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(workbook, `Scope_Master_${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      setError("Error exporting Excel");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Delete handlers ----------
  const handleDeleteClick = (scopeid) => {
    setDeleteId(scopeid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE_URL}/admin-master/scope-master/${deleteId}`);
      if (response.data.status === 'success') {
        setSuccessMessage('Scope deleted successfully!');
        fetchScopeList();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to delete scope');
      }
    } catch (err) {
      console.error('Error deleting scope:', err);
      setError(err.response?.data?.message || 'Error deleting scope');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleViewScope = (scope) => {
    setSelectedScope(scope);
    setViewDialogOpen(true);
  };

  // ---------- Bulk upload handlers ----------
  const handleBulkUpload = () => {
    setBulkDialogOpen(true);
    // Keep default lab type from localStorage
  };

  const handleBulkDialogClose = () => {
    setBulkDialogOpen(false);
    setBulkDiscipline('');
    setBulkGroup('');
    setBulkScopeType('');
    setBulkPTAvailable(false);
    setBulkFile(null);
    setBulkGroups([]);
  };

  const handleDownloadTemplate = () => {
    if (!bulkLabType) {
      setError('Please select CAB Type to download template');
      return;
    }
    const headers = ['MaterialsTested', 'ComponentParameterTested', 'TestMethodSpecification'];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ScopeTemplate');
    const cabType = LAB_TYPES.find(c => c.id === bulkLabType)?.name || 'scope';
    XLSX.writeFile(wb, `ScopeTemplate_${cabType}.xlsx`);
  };

  const parseFile = async (file) => {
    const required = ['MaterialsTested', 'ComponentParameterTested', 'TestMethodSpecification'];
    const ext = file.name.split('.').pop().toLowerCase();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (ext === 'xlsx' || ext === 'xls') {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!json.length) return reject(new Error('File is empty'));
            const headers = json[0].map(h => (h || '').toString().trim());
            const missing = required.filter(r => !headers.includes(r));
            if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
            const rows = json.slice(1).map(rowArr => {
              const obj = {};
              headers.forEach((h, idx) => { obj[h] = rowArr[idx] ? rowArr[idx].toString().trim() : ''; });
              return obj;
            }).filter(r => r.MaterialsTested);
            resolve(rows);
          } catch (e) {
            reject(new Error('Invalid Excel file'));
          }
        } else {
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
          if (lines.length === 0) return reject(new Error('File is empty'));
          const headers = lines[0].split(',').map(h => h.trim());
          const missing = required.filter(r => !headers.includes(r));
          if (missing.length) return reject(new Error('Missing headers: ' + missing.join(', ')));
          const rows = lines.slice(1).map(line => {
            const cols = line.split(',');
            const obj = {};
            headers.forEach((h, idx) => { obj[h] = cols[idx] ? cols[idx].trim() : ''; });
            return obj;
          }).filter(r => r.MaterialsTested);
          resolve(rows);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      if (ext === 'xlsx' || ext === 'xls') {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const handleBulkUploadSubmit = async () => {
    setError('');
    setSuccessMessage('');
    if (!bulkLabType) {
      setError('Please select CAB Type');
      return;
    }
    if (!bulkDiscipline) {
      setError('Please select Discipline');
      return;
    }
    if (!bulkScopeType) {
      setError('Please select Scope Type');
      return;
    }
    if (!bulkFile) {
      setError('Please choose an Excel/CSV file to upload');
      return;
    }

    try {
      setUploadLoading(true);
      const rows = await parseFile(bulkFile);
      if (!rows.length) {
        setError('No valid rows found in file');
        setUploadLoading(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;
      let failRows = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const payload = {
          labtypeid: bulkLabType,
          disciplineid: bulkDiscipline,
          disciplineName: disciplines.find(d => Number(d.id) === Number(bulkDiscipline))?.disciplineName || '',
          groupid: bulkGroup || null,
          scopetype: bulkScopeType,
          ptavailable: bulkPTAvailable,
          materialstested: row.MaterialsTested,
          componentparametertested: row.ComponentParameterTested,
          testmethodspecification: row.TestMethodSpecification
        };

        try {
          const response = await axios.post(`${API_BASE_URL}/admin-master/scope-master`, payload);
          if (response.data.status === 'success') {
            successCount++;
          } else {
            failCount++;
            failRows.push({ row: i + 2, error: response.data.message });
          }
        } catch (err) {
          failCount++;
          failRows.push({ row: i + 2, error: err.response?.data?.message || err.message });
        }
      }

      setSuccessMessage(`Bulk upload complete. Success: ${successCount}, Failed: ${failCount}`);
      if (failRows.length) {
        setError('Failed rows: ' + failRows.map(f => `Row ${f.row}: ${f.error}`).join('; '));
      }
      fetchScopeList();
      handleBulkDialogClose();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.response?.data?.message || err.message || 'Bulk upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  // ---------- Pagination handlers ----------
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---------- Render ----------
  return (
    <Box sx={{ p: 3, mt: 10, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, color: "#1976d2" }}>
          Scope Master Details
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterLabType}
              onChange={(e) => handleLabTypeFilterChange(e.target.value)}
              size="small"
              renderValue={(selected) => {
                if (selected === "") return <em>--All CAB Types--</em>;
                const cabType = LAB_TYPES.find(c => String(c.id) === String(selected));
                return cabType?.name || "Unknown";
              }}
            >
              <MenuItem value=""><em>--All CAB Types--</em></MenuItem>
              {filteredLabTypes.length > 0 ? (
                filteredLabTypes.map((cab) => (
                  <MenuItem key={cab.id} value={cab.id}>{cab.name}</MenuItem>
                ))
              ) : (
                <MenuItem disabled><em>No Lab Types Available</em></MenuItem>
              )}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterDiscipline}
              onChange={(e) => handleDisciplineFilterChange(e.target.value)}
              disabled={!filterLabType}
              size="small"
              renderValue={(selected) => {
                if (selected === "") return <em>--All Disciplines--</em>;
                const discipline = filterDisciplines.find(d => d.id === selected);
                return discipline?.disciplineName;
              }}
            >
              <MenuItem value=""><em>--All Disciplines--</em></MenuItem>
              {filterDisciplines.map((discipline) => (
                <MenuItem key={discipline.id} value={discipline.id}>
                  {discipline.disciplineName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {(filterLabType === 1 || filterLabType === 2) && (
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                displayEmpty
                value={filterGroup}
                onChange={(e) => handleGroupFilterChange(e.target.value)}
                disabled={!filterLabType || !filterDiscipline}
                size="small"
                renderValue={(selected) => {
                  if (selected === "") return <em>--All Groups--</em>;
                  const group = filterGroups.find(g => g.grpId === selected);
                  return group?.groupName;
                }}
              >
                <MenuItem value=""><em>--All Groups--</em></MenuItem>
                {filterGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Test Code"
              value={filterTestCode}
              onChange={(e) => setFilterTestCode(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Component/Parameter Tested"
              value={filterComponent}
              onChange={(e) => setFilterComponent(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Materials or Products Tested"
              value={filterMaterial}
              onChange={(e) => setFilterMaterial(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Test Method Specification"
              value={filterTestMethod}
              onChange={(e) => setFilterTestMethod(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button variant="contained" size="small" fullWidth onClick={handleSearchClick} sx={{ height: "40px" }}>
              Search
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button variant="outlined" fullWidth size="small" onClick={handleClearFilters} sx={{ height: "40px" }}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {/* Results Counter */}
        {!loading && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {totalCount > 0
                ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount} records`
                : "No records found"}
            </Typography>
            {(filterSearch || filterLabType || filterDiscipline || filterGroup) && (
              <Typography variant="body2" color="primary">Filters applied</Typography>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : scopeData.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="textSecondary" variant="body1">
              {filterLabType && filterDiscipline
                ? "No scope data found for selected filters"
                : "Please select both CAB Type and Discipline to search"}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>S.no</strong></TableCell>
                    <TableCell><strong>CAB Type</strong></TableCell>
                    <TableCell><strong>Test Code</strong></TableCell>
                    <TableCell><strong>Discipline / Group</strong></TableCell>
                    <TableCell><strong>Component/Parameter Tested</strong></TableCell>
                    <TableCell><strong>Materials or Products Tested</strong></TableCell>
                    <TableCell><strong>Test Method Specification</strong></TableCell>
                    <TableCell><strong>Scope Type</strong></TableCell>
                    <TableCell><strong>PT Available</strong></TableCell>
                    <TableCell><strong>Created On</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopeData.map((row, index) => {
                    const cabType = LAB_TYPES.find(cab => String(cab.id) === String(row.LabTypeid));
                    return (
                      <TableRow key={row.scopeid}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{cabType?.name || "N/A"}</TableCell>
                        <TableCell>{row.TestCode || "N/A"}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.DisciplineName || ""}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {row.GroupName || ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{row.TypeOfTest || "N/A"}</TableCell>
                        <TableCell>{row.GroupTested || "N/A"}</TableCell>
                        <TableCell>{row.ScopeStandard || "N/A"}</TableCell>
                        <TableCell>{row.Category || "N/A"}</TableCell>
                        <TableCell>
                          <span style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            backgroundColor: (row.PTAvailable === "1" || row.PTAvailable === "Yes") ? "#c8e6c9" : "#ffcccc",
                            color: (row.PTAvailable === "1" || row.PTAvailable === "Yes") ? "#2e7d32" : "#c62828",
                          }}>
                            {row.PTAvailable === "1" || row.PTAvailable === "Yes" ? "Yes" : "No"}
                          </span>
                        </TableCell>
                        <TableCell>{row.InsertDate ? new Date(row.InsertDate).toLocaleDateString() : "N/A"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onClose={handleBulkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Scope</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                disabled={shouldDisableDropdown(filteredLabTypes)}
                value={bulkLabType}
                onChange={(e) => {
                  setBulkLabType(e.target.value);
                  setBulkDiscipline("");
                  setBulkGroup("");
                  setBulkGroups([]);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select CAB Type--</em>;
                  return LAB_TYPES.find(c => c.id === selected)?.name || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select CAB Type--</em></MenuItem>
                {filteredLabTypes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkDiscipline}
                onChange={(e) => {
                  setBulkDiscipline(e.target.value);
                  setBulkGroup("");
                }}
                disabled={!bulkLabType}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Discipline--</em>;
                  const d = disciplines.find(dd => dd.id === selected);
                  return d?.disciplineName || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select Discipline--</em></MenuItem>
                {disciplines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.disciplineName}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={12}>
              <Select
                fullWidth
                displayEmpty
                value={bulkGroup}
                onChange={(e) => setBulkGroup(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Group--</em>;
                  const g = bulkGroups.find(gg => gg.grpId === selected);
                  return g?.groupName || "N/A";
                }}
              >
                <MenuItem value=""><em>--Select Group--</em></MenuItem>
                {bulkGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkScopeType}
                onChange={(e) => setBulkScopeType(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Scope Type--</em>;
                  return selected;
                }}
              >
                <MenuItem value=""><em>--Select Scope Type--</em></MenuItem>
                {scopeTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkPTAvailable.toString()}
                onChange={(e) => setBulkPTAvailable(e.target.value === "true")}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (selected === "true") return "PT Available: Yes";
                  if (selected === "false") return "PT Available: No";
                  return <em>--Select PT Available--</em>;
                }}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={handleDownloadTemplate} fullWidth startIcon={<DownloadIcon />}>
                Download Template
              </Button>
            </Grid>
            <Grid item xs={12}>
              <input
                accept=".xlsx,.xls,.csv"
                style={{ display: "block" }}
                id="bulk-file-input"
                type="file"
                onChange={(e) => setBulkFile(e.target.files[0])}
              />
              {bulkFile && <Typography variant="body2" sx={{ mt: 1 }}>{bulkFile.name}</Typography>}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Note: The selected CAB Type, Discipline, Group, Scope Type and PT Available above will be applied to all rows in the uploaded file.
                Excel should contain: MaterialsTested, ComponentParameterTested, TestMethodSpecification.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDialogClose} disabled={uploadLoading}>Cancel</Button>
          <Button
            onClick={handleBulkUploadSubmit}
            variant="contained"
            disabled={uploadLoading || !bulkFile || !bulkLabType || !bulkDiscipline || !bulkScopeType}
            startIcon={uploadLoading ? <CircularProgress size={18} /> : <UploadIcon />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Scope Details</DialogTitle>
        <DialogContent dividers>
          {selectedScope && (
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: 600, width: "40%" }}>CAB Type</TableCell><TableCell>{selectedScope.LabTypeName || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell><TableCell>{selectedScope.DisciplineName || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Scope Type</TableCell><TableCell>{selectedScope.Category || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>PT Available</TableCell><TableCell>{selectedScope.PTAvailable ? "Yes" : "No"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Component/Parameter Tested</TableCell><TableCell>{selectedScope.TypeOfTest || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Materials or Products Tested</TableCell><TableCell>{selectedScope.GroupTested || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Test Method Specification</TableCell><TableCell>{selectedScope.ScopeStandard || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Group</TableCell><TableCell>{selectedScope.GroupName || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Sub Group</TableCell><TableCell>{selectedScope.SubGroupadd || "N/A"}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Created On</TableCell><TableCell>{selectedScope.InsertDate ? new Date(selectedScope.InsertDate).toLocaleString() : "N/A"}</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this scope?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScopeMasterPage;